import { apiClient, type PaginatedResponse, APIError } from '../../../api/client'
import { GeneralfilesApi } from '../../../generated-types/apis/GeneralfilesApi'
import { RequestrequestFileApi } from '../../../generated-types/apis/RequestrequestFileApi'
import { ItemsarticleFileApi } from '../../../generated-types/apis/ItemsarticleFileApi'
import { ContractcontractFileApi } from '../../../generated-types/apis/ContractcontractFileApi'
import { ItemscontractFileApi } from '../../../generated-types/apis/ItemscontractFileApi'
import { RequestofferFileApi } from '../../../generated-types/apis/RequestofferFileApi'
import { ContractbillFileApi } from '../../../generated-types/apis/ContractbillFileApi'
import { GuestRequestrequestFileApi } from '../../../generated-types/apis/GuestRequestrequestFileApi'
import { Configuration } from '../../../generated-types/runtime'
import Cookies from 'js-cookie'
import LogTool from '../../../logger/logTools'
import { type FileMetadata, type Folder } from '../utils/types'
import { type FileInfo } from '../../../utils/types'
import { createFileInfo, getPKfromSelf } from '../../../utils/functions'
import buildApiUrl, { type PaginationOptions } from '../../../api/pagination'
import { type GenericFilterType, type PaginatedQueryParameters } from '../../../api/hooks/utils/types'
import { searchArticleFiles, type ArticleFileSearchFilters } from './articleFileSearchApi'
import { searchRequestFiles, type RequestFileSearchFilters } from './requestFileSearchApi'
import { searchOfferFiles, type OfferFileSearchFilters } from './offerFileSearchApi'
import { searchContractFiles, type ContractFileSearchFilters } from './contractFileSearchApi'
import { searchBillFiles, type BillFileSearchFilters } from './billFileSearchApi'
import { searchGuestRequestFiles, type GuestRequestFileSearchFilters } from './guestRequestFileSearchApi'

const log = new LogTool({ context: 'FileAPI', enable: true, logLevel: 'warn' })

/**
 * Get API configuration with authentication token
 */
function getApiConfiguration(): Configuration {
  const envApiUrl = process.env.REACT_APP_API_URL
  if (!envApiUrl) {
    throw new Error('API URL is not defined. Please set REACT_APP_API_URL environment variable.')
  }
  
  return new Configuration({
    basePath: envApiUrl.endsWith('/') ? envApiUrl.slice(0, -1) : envApiUrl,
    accessToken: () => {
      const token = Cookies.get('access')
      return token || ''
    }
  })
}

/**
 * Get API instances
 */
function getGeneralFilesApi(): GeneralfilesApi {
  return new GeneralfilesApi(getApiConfiguration())
}

function getRequestFilesApi(): RequestrequestFileApi {
  return new RequestrequestFileApi(getApiConfiguration())
}

function getArticleFilesApi(): ItemsarticleFileApi {
  return new ItemsarticleFileApi(getApiConfiguration())
}

function getContractFilesApi(): ContractcontractFileApi {
  return new ContractcontractFileApi(getApiConfiguration())
}

function getItemsContractFilesApi(): ItemscontractFileApi {
  return new ItemscontractFileApi(getApiConfiguration())
}

function getOfferFilesApi(): RequestofferFileApi {
  return new RequestofferFileApi(getApiConfiguration())
}

function getBillFilesApi(): ContractbillFileApi {
  return new ContractbillFileApi(getApiConfiguration())
}

function getGuestRequestFilesApi(): GuestRequestrequestFileApi {
  return new GuestRequestrequestFileApi(getApiConfiguration())
}

export interface FilesystemResponse {
  directories: Folder[]
  files: FileMetadata[]
}

/**
 * Fetch files in a folder
 * Note: Uses apiClient because GeneralfilesApi doesn't have a proper list method with path parameter
 */
export async function fetchFilesInFolder(path = ''): Promise<FilesystemResponse> {
  log.info('Fetching files in folder:', path)
  const response = await apiClient.get<FilesystemResponse>(`v1/general/files/?path=${path}`)
  return response
}

/**
 * Download a file
 * Note: Uses apiClient for blob download
 */
export async function downloadFile(filePath: string): Promise<Blob> {
  log.info('Downloading file:', filePath)
  const response = await apiClient.downloadBlob(`v1/general/files${filePath}`)
  return response
}

/**
 * Delete a file (general filesystem)
 * Note: Uses apiClient for custom path-based deletion
 */
export async function deleteFile(filePath: string): Promise<void> {
  log.info('Deleting file:', filePath)
  const isAbsoluteUrl = /^https?:\/\//i.test(filePath)
  const isApiPath = filePath.startsWith('/v1/') || filePath.startsWith('v1/')
  const target = (isAbsoluteUrl || isApiPath)
    ? filePath
    : `v1/general/files${filePath}`
  await apiClient.delete(target)
}

/**
 * Delete a folder
 * Note: Uses apiClient for custom path-based deletion
 */
export async function deleteFolder(folderPath: string): Promise<void> {
  log.info('Deleting folder:', folderPath)
  const path = folderPath.endsWith('/') ? folderPath : `${folderPath}/`
  await apiClient.delete(`v1/general/files${path}`)
}

/**
 * Upload a file to general filesystem
 * Note: Uses apiClient because GeneralfilesApi doesn't support path parameter properly
 */
export async function uploadFile(
  path: string,
  file: File
): Promise<any> {
  log.info('Uploading file:', file.name, 'to path:', path)
  const formData = new FormData()
  formData.append('file', file)
  formData.append('path', path)
  const response = await apiClient.post<any>('v1/general/files/', formData, undefined, true)
  return response
}

/**
 * Upload a file to general filesystem (used by S3FileUpload component)
 * Note: Uses apiClient for FormData upload with custom path handling
 */
export async function uploadFileToGeneralFiles(path: string, file: File): Promise<any> {
  log.info('Uploading file to general files:', file.name, 'to path:', path)
  const formData = new FormData()
  const filePath = path ? `${path}/${file.name}` : file.name
  formData.append('file', filePath)
  formData.append('file', file)
  const apiUrl = process.env.REACT_APP_API_URL
  if (!apiUrl) throw new Error('no api url')
  const response = await fetch(`${apiUrl}general/files/`, {
    method: 'POST',
    body: formData,
    headers: {
      Accept: '*/*',
      'Content-Disposition': "form-data; name='file';",
      Authorization: `Bearer ${Cookies.get('access') || ''}`,
    },
  })
  if (!response.ok) {
    throw new Error(`Upload failed: ${response.statusText}`)
  }
  const data: unknown = await response.json()
  return data
}

const ensureTrailingSlash = (url: string): string =>
  url.endsWith('/') ? url : `${url}/`

export async function convertArticleFileToScs(fileSelf: string): Promise<void> {
  const target = `${ensureTrailingSlash(fileSelf)}convert_scs/`
  await apiClient.post(target, {})
}

/**
 * Create a folder
 * Note: Folders are created by creating a file path that ends with "/"
 * Uses apiClient for multipart/form-data with custom path handling
 */
export async function createFolder(
  path: string,
  folderName: string
): Promise<any> {
  log.info('Creating folder:', folderName, 'in path:', path)
  const formData = new FormData()
  const folderPath = path + '/' + folderName + '/'
  formData.append('path', folderPath)
  const response = await apiClient.post<any>('v1/general/files/', formData, undefined, true)
  return response
}

/**
 * Extract ID from a file URL
 */
function extractFileIdFromUrl(url: string): number {
  try {
    // Try to extract ID from URL like /v1/request/files/123/ or /files/123/
    // Also supports patterns like /offer_files/123/, /request_files/123/, /bill_files/123/
    const match = url.match(/\/(?:offer_files|request_files|bill_files|files)\/(\d+)\/?$/)
    if (match && match[1]) {
      return parseInt(match[1], 10)
    }
    // Try getPKfromSelf as fallback
    const pk = getPKfromSelf(url)
    return parseInt(pk, 10)
  } catch (error) {
    throw new Error(`Could not extract file ID from URL: ${url}`)
  }
}

/**
 * Extract resource ID from a files list URL
 */
function extractResourceIdFromFilesUrl(url: string): number {
  try {
    // Match patterns like /v1/items/articles/123/files/ or /articles/123/files/
    const match = url.match(/\/(\d+)\/files\/?$/)
    if (match && match[1]) {
      return parseInt(match[1], 10)
    }
    throw new Error(`Could not extract resource ID from URL: ${url}`)
  } catch (error) {
    throw new Error(`Could not extract resource ID from URL: ${url}`)
  }
}

/**
 * Determine which API to use based on URL pattern
 */
export function determineApiFromUrl(url: string): {
  type: 'request' | 'article' | 'contract' | 'offer' | 'bill' | 'guestRequest' | 'unknown'
  resourceId?: number
} {
  // Check flat endpoint patterns first (before nested patterns)
  if (url.includes('/request/offer_files/')) {
    return { type: 'offer', resourceId: undefined }
  }
  if (url.includes('/request/request_files/')) {
    return { type: 'request', resourceId: undefined }
  }
  if (url.includes('/contract/bill_files/')) {
    return { type: 'bill', resourceId: undefined }
  }

  if (url.includes('/request/files/') || url.includes('/requests/')) {
    if (url.includes('/guest-request/') || url.includes('/guest_request/')) {
      const resourceId = extractResourceIdFromFilesUrl(url.replace(/.*\/guest[-_]request\/\d+\//, ''))
      return { type: 'guestRequest', resourceId }
    }
    const resourceId = url.includes('/requests/') ? extractResourceIdFromFilesUrl(url) : undefined
    return { type: 'request', resourceId }
  }
  if (url.includes('/items/articles/') || url.includes('/articles/')) {
    const resourceId = extractResourceIdFromFilesUrl(url)
    return { type: 'article', resourceId }
  }
  if (url.includes('/contract/contracts/') || url.includes('/contracts/')) {
    const resourceId = extractResourceIdFromFilesUrl(url)
    return { type: 'contract', resourceId }
  }
  if (url.includes('/offer/files/') || url.includes('/offers/')) {
    const resourceId = url.includes('/offers/') ? extractResourceIdFromFilesUrl(url) : undefined
    return { type: 'offer', resourceId }
  }
  if (url.includes('/bill/files/') || url.includes('/bills/')) {
    const resourceId = url.includes('/bills/') ? extractResourceIdFromFilesUrl(url) : undefined
    return { type: 'bill', resourceId }
  }
  return { type: 'unknown' }
}

/**
 * Fetch file list from a specific URL (supports pagination and search)
 * Note: This is for fetching file lists from URLs like article files, contract files, etc.
 * Uses appropriate generated API where possible, falls back to apiClient for flexibility
 * If filters contain a 'q' parameter, uses Elasticsearch search APIs instead
 */
type FileListOptions = (PaginatedQueryParameters<FileInfo, GenericFilterType> | PaginationOptions) & {
  accessSecret?: string
}

export async function fetchFileList(
  url: string,
  options: FileListOptions = {}
): Promise<PaginatedResponse<FileInfo>> {
  log.info('Fetching file list from:', url)

  const accessSecret = options.accessSecret ?? Cookies.get('access_secret')
  const secretHeaders = accessSecret ? { 'X-ACCESS-SECRET': accessSecret } : undefined

  // Check if the url leads to a correct API endpoint
  if (!url.match(/files\//)) {
    throw new Error(`The provided URL ${url} does not lead to the correct API endpoint!`)
  }

  const apiInfo = determineApiFromUrl(url)
  const filters = (options as PaginatedQueryParameters<FileInfo, GenericFilterType>).filters ?? {}
  const parseFileSizeFilter = (value: unknown): string | undefined => {
    if (typeof value === 'string') {
      const trimmed = value.trim()
      return trimmed === '' ? undefined : trimmed
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      // Convert number to string for backward compatibility
      return String(value)
    }
    return undefined
  }
  const fileSizeValue = parseFileSizeFilter(filters.file_size ?? filters.fileSize ?? filters.size)
  
  // Check if search query exists - if so, use search APIs
  // Treat 'q' parameter as valid even if it's '*' (wildcard search)
  // This allows search APIs to be used when filters are present
  const rawSearchQuery = typeof filters.q === 'string' ? filters.q : ''
  const searchQuery = rawSearchQuery.trim()
  // Use search APIs when q parameter exists and is not empty (empty string means no search)
  // '*' is treated as a valid wildcard search query
  const hasSearchQuery = 'q' in filters && searchQuery !== ''

  if (hasSearchQuery) {
    // Use search APIs when search query is provided
    const page = (options as PaginatedQueryParameters<FileInfo, GenericFilterType>).page ?? 1
    const pageSize = (options as PaginatedQueryParameters<FileInfo, GenericFilterType>).pageSize ?? 20
    const from = (page - 1) * pageSize
    const size = pageSize

    try {
      if (apiInfo.type === 'article') {
        const fileNameValue = typeof filters.file_name === 'string' ? filters.file_name : typeof filters.fileName === 'string' ? filters.fileName : typeof filters.name === 'string' ? filters.name : undefined
        const searchFilters: ArticleFileSearchFilters = {
          q: searchQuery,
          articleId: apiInfo.resourceId,
          fileName: fileNameValue,
          storedAt: typeof filters.stored_at === 'string' ? filters.stored_at : undefined,
          fileSize: fileSizeValue,
          from,
          size,
        }
        const searchResponse = await searchArticleFiles(searchFilters)
        return {
          count: searchResponse.total,
          next: null, // Search APIs use offset-based pagination, URLs are not provided
          previous: null,
          results: searchResponse.results,
        }
      } else if (apiInfo.type === 'request') {
        const fileNameValue = typeof filters.file_name === 'string' ? filters.file_name : typeof filters.fileName === 'string' ? filters.fileName : typeof filters.name === 'string' ? filters.name : undefined
        const searchFilters: RequestFileSearchFilters = {
          q: searchQuery,
          requestId: apiInfo.resourceId,
          requestCertificate: typeof filters.request_certificate === 'string' ? filters.request_certificate : undefined,
          fileName: fileNameValue,
          storedAt: typeof filters.stored_at === 'string' ? filters.stored_at : undefined,
          fileSize: fileSizeValue,
          from,
          size,
        }
        const searchResponse = await searchRequestFiles(searchFilters)
        return {
          count: searchResponse.total,
          next: null, // Search APIs use offset-based pagination, URLs are not provided
          previous: null,
          results: searchResponse.results,
        }
      } else if (apiInfo.type === 'offer') {
        const fileNameValue = typeof filters.file_name === 'string' ? filters.file_name : typeof filters.fileName === 'string' ? filters.fileName : typeof filters.name === 'string' ? filters.name : undefined
        const searchFilters: OfferFileSearchFilters = {
          q: searchQuery,
          offerId: apiInfo.resourceId,
          requestCertificate: typeof filters.request_certificate === 'string' ? filters.request_certificate : undefined,
          fileName: fileNameValue,
          storedAt: typeof filters.stored_at === 'string' ? filters.stored_at : undefined,
          fileSize: fileSizeValue,
          from,
          size,
        }
        const searchResponse = await searchOfferFiles(searchFilters)
        return {
          count: searchResponse.total,
          next: null, // Search APIs use offset-based pagination, URLs are not provided
          previous: null,
          results: searchResponse.results,
        }
      } else if (apiInfo.type === 'contract') {
        const fileNameValue = typeof filters.file_name === 'string' ? filters.file_name : typeof filters.fileName === 'string' ? filters.fileName : typeof filters.name === 'string' ? filters.name : undefined
        const searchFilters: ContractFileSearchFilters = {
          q: searchQuery,
          contractId: apiInfo.resourceId,
          requestCertificate: typeof filters.request_certificate === 'string' ? filters.request_certificate : undefined,
          fileName: fileNameValue,
          storedAt: typeof filters.stored_at === 'string' ? filters.stored_at : undefined,
          fileSize: fileSizeValue,
          from,
          size,
        }
        const searchResponse = await searchContractFiles(searchFilters)
        return {
          count: searchResponse.total,
          next: null, // Search APIs use offset-based pagination, URLs are not provided
          previous: null,
          results: searchResponse.results,
        }
      } else if (apiInfo.type === 'bill') {
        const fileNameValue = typeof filters.file_name === 'string' ? filters.file_name : typeof filters.fileName === 'string' ? filters.fileName : typeof filters.name === 'string' ? filters.name : undefined
        const searchFilters: BillFileSearchFilters = {
          q: searchQuery,
          billId: apiInfo.resourceId,
          requestCertificate: typeof filters.request_certificate === 'string' ? filters.request_certificate : undefined,
          fileName: fileNameValue,
          storedAt: typeof filters.stored_at === 'string' ? filters.stored_at : undefined,
          fileSize: fileSizeValue,
          from,
          size,
        }
        const searchResponse = await searchBillFiles(searchFilters)
        return {
          count: searchResponse.total,
          next: null, // Search APIs use offset-based pagination, URLs are not provided
          previous: null,
          results: searchResponse.results,
        }
      } else if (apiInfo.type === 'guestRequest') {
        const fileNameValue = typeof filters.file_name === 'string' ? filters.file_name : typeof filters.fileName === 'string' ? filters.fileName : typeof filters.name === 'string' ? filters.name : undefined
        const searchFilters: GuestRequestFileSearchFilters = {
          q: searchQuery,
          guestRequestId: apiInfo.resourceId,
          fileType: typeof filters.file_type === 'string' ? filters.file_type : undefined,
          fileName: fileNameValue,
          createdAt: typeof filters.created_at === 'string' ? filters.created_at : undefined,
          fileSize: fileSizeValue,
          from,
          size,
        }
        const searchResponse = await searchGuestRequestFiles(searchFilters)
        return {
          count: searchResponse.total,
          next: null, // Search APIs use offset-based pagination, URLs are not provided
          previous: null,
          results: searchResponse.results,
        }
      }
    } catch (error) {
      log.warn('File search API failed, falling back to regular list API', error)
      // Fall through to regular list API
    }
  }

  // Regular list API (no search query or search API failed)
  const fetchUrl = buildApiUrl(
    url,
    options as PaginatedQueryParameters<FileInfo, GenericFilterType>
  )

  let response: PaginatedResponse<any>

  try {
    // Try to use generated API for specific resource types
    if (!secretHeaders && apiInfo.type === 'article' && apiInfo.resourceId !== undefined) {
      const page = (options as PaginatedQueryParameters<FileInfo, GenericFilterType>).page ?? 1
      const pageSize = (options as PaginatedQueryParameters<FileInfo, GenericFilterType>).pageSize ?? 10
      const apiResponse = await getArticleFilesApi().v1ItemsArticlesFilesList({
        articlePk: apiInfo.resourceId,
        page,
        pageSize,
      })
      response = {
        count: apiResponse.count ?? 0,
        next: apiResponse.next ?? null,
        previous: apiResponse.previous ?? null,
        results: apiResponse.results ?? [],
      }
    } else if (!secretHeaders && apiInfo.type === 'contract' && apiInfo.resourceId !== undefined) {
      const page = (options as PaginatedQueryParameters<FileInfo, GenericFilterType>).page ?? 1
      const pageSize = (options as PaginatedQueryParameters<FileInfo, GenericFilterType>).pageSize ?? 10
      const apiResponse = await getContractFilesApi().v1ContractContractsFilesList({
        contractPk: apiInfo.resourceId,
        page,
        pageSize,
      })
      response = {
        count: apiResponse.count ?? 0,
        next: apiResponse.next ?? null,
        previous: apiResponse.previous ?? null,
        results: apiResponse.results ?? [],
      }
    } else {
      // Fall back to apiClient for other cases or when we need more flexibility
      response = await apiClient.get<PaginatedResponse<any>>(fetchUrl, secretHeaders)
    }
  } catch (error) {
    // If generated API fails, fall back to apiClient
    log.warn('Generated API call failed, falling back to apiClient', error)
    response = await apiClient.get<PaginatedResponse<any>>(fetchUrl, secretHeaders)
  }

  const files = response.results.map((fileResponse: unknown) => createFileInfo(fileResponse))

  return {
    count: response.count || 0,
    next: response.next || null,
    previous: response.previous || null,
    results: files,
  }
}

/**
 * Options for uploading a file to a URL
 */
export interface UploadFileOptions {
  /** Optional access secret for temporary request file uploads */
  accessSecret?: string
}

/**
 * Upload a file to a specific URL endpoint
 * Note: This is for uploading files to specific resource endpoints (articles, contracts, etc.)
 * Uses appropriate generated API where possible
 */
export async function uploadFileToUrl(
  url: string,
  file: File,
  options: UploadFileOptions = {}
): Promise<FileInfo> {
  log.info('Uploading file to URL:', url, 'File:', file.name)

  // Check if the url leads to an API endpoint that is intended to accept files
  if (!(url.match(/files\//) || url.match(/import/))) {
    throw new Error(
      `The provided URL ${url} does not lead to an API endpoint that is intended to accept file uploads!`
    )
  }

  const apiInfo = determineApiFromUrl(url)
  const { accessSecret } = options
  const secretHeaders = accessSecret ? { 'X-ACCESS-SECRET': accessSecret } : undefined
  let response: unknown

  try {
    // Try to use generated API for specific resource types
    // Note: Generated APIs don't support custom headers, so we fall back to apiClient when accessSecret is provided
    if (!accessSecret && apiInfo.type === 'article' && apiInfo.resourceId !== undefined) {
      const formData = new FormData()
      formData.append('document', file)
      // Extract article URL from the files URL
      const articleUrl = url.replace(/\/files\/?$/, '/')
      formData.append('article', articleUrl)
      const apiResponse = await getArticleFilesApi().v1ItemsArticlesFilesCreate({
        articlePk: apiInfo.resourceId,
        article: articleUrl,
        document: file,
      })
      response = apiResponse
    } else if (!accessSecret && apiInfo.type === 'contract' && apiInfo.resourceId !== undefined) {
      const contractUrl = url.replace(/\/files\/?$/, '/')
      const apiResponse = await getItemsContractFilesApi().v1ContractContractsFilesCreate({
        contractPk: apiInfo.resourceId,
        contract: contractUrl,
        document: file,
      })
      response = apiResponse
    } else if (!accessSecret && apiInfo.type === 'request') {
      const requestUrl = url.includes('/requests/')
        ? url.replace(/\/files\/?$/, '/')
        : url.replace(/\/files\/?$/, '/').replace(/request\/files/, 'request/requests')
      const apiResponse = await getRequestFilesApi().v1RequestFilesCreate({
        request: requestUrl,
        document: file,
      })
      response = apiResponse
    } else {
      // Fall back to apiClient for other cases or when accessSecret is provided
      const fileFormData: FormData = new FormData()
      fileFormData.append('document', file)
      response = await apiClient.post<any>(url, fileFormData, {
        Accept: '*/*',
        ...secretHeaders,
      }, true)
    }
  } catch (error) {
    // If generated API fails, fall back to apiClient
    log.warn('Generated API call failed, falling back to apiClient', error)
    const fileFormData: FormData = new FormData()
    fileFormData.append('document', file)
    response = await apiClient.post<any>(url, fileFormData, {
      Accept: '*/*',
      ...secretHeaders,
    }, true)
  }

  return createFileInfo(response)
}

/**
 * Delete a file from a specific URL
 * Enhanced to support both path-based and URL-based deletion
 * Uses appropriate generated API where possible
 */
export async function deleteFileFromUrl(url: string): Promise<void> {
  log.info('Deleting file from URL:', url)

  // Check if the url leads to an API endpoint that holds any files
  // Supports both /files/ and *_files/ patterns (e.g., offer_files/, request_files/, bill_files/)
  if (!url.match(/\/(?:offer_files|request_files|bill_files|files)\//)) {
    throw new Error(`The provided URL ${url} does not lead to an API endpoint that holds any files!`)
  }

  try {
    const apiInfo = determineApiFromUrl(url)
    const fileId = extractFileIdFromUrl(url)

    // Try to use generated API for specific resource types
    if (apiInfo.type === 'article' && apiInfo.resourceId !== undefined) {
      await getArticleFilesApi().v1ItemsArticlesFilesDestroy({
        articlePk: apiInfo.resourceId,
        id: fileId,
      })
      return
    } else if (apiInfo.type === 'contract' && apiInfo.resourceId !== undefined) {
      await getItemsContractFilesApi().v1ContractContractsFilesDestroy({
        contractPk: apiInfo.resourceId,
        id: fileId,
      })
      return
    } else if (apiInfo.type === 'request') {
      await getRequestFilesApi().v1RequestFilesDestroy({
        id: fileId,
      })
      return
    } else if (apiInfo.type === 'offer') {
      await getOfferFilesApi().v1RequestOfferFilesDestroy({
        id: fileId,
      })
      return
    } else if (apiInfo.type === 'bill') {
      await getBillFilesApi().v1ContractBillFilesDestroy({
        id: fileId,
      })
      return
    } else if (apiInfo.type === 'guestRequest') {
      await getGuestRequestFilesApi().v1GuestRequestFilesDestroy({
        id: fileId,
      })
      return
    }

    // Fall back to apiClient for other cases
    await apiClient.delete(url)
  } catch (error) {
    // Handle 403 Forbidden errors with user-friendly message
    if (error instanceof APIError && error.status === 403) {
      throw new Error(
        'You do not have permission to delete this file. Staff users cannot delete article files from other organizations that are used in requests.'
      )
    }
    // Re-throw other errors as-is
    throw error
  }
}
